import { _filterNullishValues, _objectKeys, _omit, _sortBy, _stringMapEntries } from '@naturalcycles/js-lib';
import { HardwareId, Mens } from '../index.js';
import { DataFlag } from './dataFlag.shared.model.js';
const NON_DATA_ENTRY_KEYS = new Set(['date', 'updated', 'temperatureUpdatedTimestamp', 'temperatureMeasuredTimestamp', 'dataFlags',
// checked separately
'lhTestImageIds',
// checked separately
'dataFlagIntensity',
// checked separately
'source', 'newGoal', 'newHwId', 'noLongerPregnant' // todo: to be reconsidered when making changes to startDate for postpartum users
]);
const NON_DATA_ENTRY_KEYS_AND_SKIPPED = new Set([...NON_DATA_ENTRY_KEYS, 'skipped']);
export const APP_UNSUPPORTED_ENTRY_KEYS = new Set(['temperatureMap', 'hwId', 'rawTemperature', 'newGoal', 'newHwId',
// todo: consider deprecating `backendResponse.hwChanges` in favor of this property
'noLongerPregnant']);
const DATA_FLAGS_USED_BY_ALGO = new Set([DataFlag.DEVIATION_REASON_OTHER, DataFlag.DEVIATION_REASON_ALCOHOL, DataFlag.DEVIATION_REASON_SICK, DataFlag.DEVIATION_REASON_SLEEP, DataFlag.OURA_INCOMPLETE_DATA, DataFlag.OURA_IRREGULAR_SLEEP, DataFlag.MORE_EMERGENCY_PILL,
// todo: Magda: keeping it here for now even though not used by the algo... need to think
DataFlag.MORE_EMERGENCY_IUD]);
function booleanSer(v) {
  return v ? '1' : 0;
}
const EMPTY_TOKEN = 0;
class DailyEntrySharedUtil {
  /**
   * Returned DailyEntryBM[] is sorted by date asc.
   */
  parseDailyEntriesString(entries) {
    if (!entries) return [];
    const rawEntries = JSON.parse(entries);
    return _sortBy(rawEntries.map(r => this.parseRawDailyEntry(r)), de => de.date, true);
  }
  /**
   * Entries will be sorted by date (input is not mutated).
   */
  serializeToDailyEntriesString(entries) {
    const rawEntries = _sortBy(entries, de => de.date).map(de => this.serializeToRawDailyEntry(de));
    return JSON.stringify(rawEntries);
  }
  parseRawDailyEntry(r) {
    if (!r) return undefined;
    const de = {
      date: r[0],
      dataFlags: [],
      temperatureMap: {}
    };
    if (r[1]) de.updated = Number(r[1]);
    if (r[2]) de.skipped = true;
    // if (r[3]) de.deviationReason = deviationReasonByCode.get(Number(r[3])) // field removed, replaced by dataFlags
    if (r[4]) de.temperature = Number(r[4]);
    if (r[5]) de.lhTest = Number(r[5]);
    if (r[6]) de.mens = Number(r[6]);
    if (r[7]) de.pregTest = Number(r[7]);
    if (r[8]) de.sex = Number(r[8]);
    if (r[9]) de.source = r[9];
    if (r[10]) de.notes = r[10];
    // if (r[11]) de.firstAddedByUser = r[11] // field removed
    if (r[12]) {
      de.dataFlags = r[12].filter(Boolean);
    }
    if (r[13]) de.mensQuantity = Number(r[13]);
    if (r[14]) de.cervicalMucusConsistency = Number(r[14]);
    if (r[15]) de.cervicalMucusQuantity = Number(r[15]);
    if (r[16]) de.sexType = Number(r[16]);
    if (r[17]) de.libido = Number(r[17]);
    if (r[18]) de.covidTest = Number(r[18]);
    if (r[19]) de.hwId = r[19];
    if (r[20]) de.temperatureMap[HardwareId.ORAL_THERMOMETER] = r[20];
    if (r[21]) de.temperatureMap[HardwareId.OURA] = r[21];
    if (r[22]) de.temperatureUpdatedTimestamp = Number(r[22]);
    if (r[23]) de.temperatureMeasuredTimestamp = Number(r[23]);
    if (r[24]) de.lhTestImageIds = r[24];
    if (r[25]) de.rawTemperature = Number(r[25]);
    if (r[26]) de.temperatureMap[HardwareId.UEBE_THERMOMETER] = r[26];
    if (r[27]) de.temperatureMap[HardwareId.T3_THERMOMETER] = r[27];
    if (r[28]) de.temperatureMap[HardwareId.APPLE_WATCH] = r[28];
    if (r[29]) de.dataFlagIntensity = r[29];
    if (r[30]) de.temperatureMap[HardwareId.B1] = r[30];
    if (r[31]) de.sleep = r[31];
    if (r[32]) de.temperatureMap[HardwareId.SAMSUNG_RING] = r[32];
    if (r[33]) de.newGoal = r[33];
    if (r[34]) de.newHwId = r[34];
    if (r[35]) de.noLongerPregnant = true;
    if (r[36]) de.babyFeeding = r[36];
    if (Object.keys(de.temperatureMap).length === 0) {
      // todo: set to undefined instead
      delete de.temperatureMap; // don't return empty map
    } else {
      if (de.temperature && de.hwId) de.temperatureMap[de.hwId] = de.temperature;
    }
    return de;
  }
  // eslint-disable-next-line complexity
  serializeToRawDailyEntry(de) {
    const a = [de.date,
    // 0
    de.updated || EMPTY_TOKEN,
    // 1
    booleanSer(de.skipped),
    // 2
    EMPTY_TOKEN,
    // dataFlagsToLegacyDeviationReason(de.dataFlags) || '', // 3, field removed
    de.temperature || EMPTY_TOKEN,
    // 4
    de.lhTest || EMPTY_TOKEN,
    // 5
    de.mens || EMPTY_TOKEN,
    // 6
    de.pregTest || EMPTY_TOKEN,
    // 7
    de.sex || EMPTY_TOKEN,
    // 8
    de.source || EMPTY_TOKEN,
    // 9
    de.notes || EMPTY_TOKEN,
    // 10
    EMPTY_TOKEN,
    // 11, firstAddedByUser was removed, but left a gap in our hearts and our data model
    de.dataFlags?.length ? de.dataFlags.filter(Boolean) : EMPTY_TOKEN,
    // 12
    de.mensQuantity || EMPTY_TOKEN,
    // 13
    de.cervicalMucusConsistency || EMPTY_TOKEN,
    // 14
    de.cervicalMucusQuantity || EMPTY_TOKEN,
    // 15
    de.sexType || EMPTY_TOKEN,
    // 16
    de.libido || EMPTY_TOKEN,
    // 17
    de.covidTest || EMPTY_TOKEN,
    // 18
    // Save ORAL_THERMOMETER as empty, because it's default
    // Only allow hwId if there's a temperature (otherwise - skip it as empty)
    de.hwId && de.hwId !== HardwareId.ORAL_THERMOMETER && de.temperature ? de.hwId : EMPTY_TOKEN,
    // 19
    de.temperatureMap?.[HardwareId.ORAL_THERMOMETER] || EMPTY_TOKEN,
    // 20
    de.temperatureMap?.[HardwareId.OURA] || EMPTY_TOKEN,
    // 21
    de.temperatureUpdatedTimestamp || EMPTY_TOKEN,
    // 22
    de.temperatureMeasuredTimestamp || EMPTY_TOKEN,
    // 23
    de.lhTestImageIds?.length ? de.lhTestImageIds : EMPTY_TOKEN,
    // 24
    de.rawTemperature || EMPTY_TOKEN,
    // 25
    de.temperatureMap?.[HardwareId.UEBE_THERMOMETER] || EMPTY_TOKEN,
    // 26
    de.temperatureMap?.[HardwareId.T3_THERMOMETER] || EMPTY_TOKEN,
    // 27
    de.temperatureMap?.[HardwareId.APPLE_WATCH] || EMPTY_TOKEN,
    // 28
    de.dataFlagIntensity && _objectKeys(de.dataFlagIntensity).length ? de.dataFlagIntensity : EMPTY_TOKEN,
    // 29
    de.temperatureMap?.[HardwareId.B1] || EMPTY_TOKEN,
    // 30
    de.sleep || EMPTY_TOKEN,
    // 31
    de.temperatureMap?.[HardwareId.SAMSUNG_RING] || EMPTY_TOKEN,
    // 32
    de.newGoal || EMPTY_TOKEN,
    // 33
    de.newHwId || EMPTY_TOKEN,
    // 34
    booleanSer(de.noLongerPregnant),
    // 35
    de.babyFeeding || EMPTY_TOKEN // 36
    ];
    return emptyTailOpimize(a);
  }
  /**
   * Function currently does nothing (except _filterNullish),
   * cause we get rid of `toCode/fromCode` conversion in favour of
   * "number enums".
   */
  dailyEntryToSaveInput(de) {
    return _filterNullishValues(de);
  }
  saveInputToDailyEntry(input) {
    const r = {
      dataFlags: [],
      ...input,
      dataFlagIntensity: input.dataFlagIntensity && _filterNullishValues(input.dataFlagIntensity)
    };
    return _filterNullishValues(r, true);
  }
  /**
   * Has data or "skipped == true".
   */
  hasDataOrSkipped(de) {
    return !!de.dataFlags?.length || !!de.lhTestImageIds?.length || !!_objectKeys(de.dataFlagIntensity || {}).length || Object.entries(de).some(([k, v]) => v && !NON_DATA_ENTRY_KEYS.has(k));
  }
  /**
   * No data, no "skipped == true".
   * Empty Entries are the same as non-existing (and are stipped away from DailyEntries, if found).
   */
  isEmpty(de) {
    return !this.hasDataOrSkipped(de);
  }
  /**
   * Data, but no "skipped == true", ignores temperatureMap
   */
  hasDataExceptSkipped(de) {
    return !!de.dataFlags?.length || !!de.lhTestImageIds?.length || !!_objectKeys(de.dataFlagIntensity || {}).length || Object.entries(_omit(de, ['temperatureMap', 'rawTemperature'])).some(([k, v]) => v && !NON_DATA_ENTRY_KEYS_AND_SKIPPED.has(k));
  }
  /**
   * No data, but can have "skipped == true"
   */
  isEmptyOrSkipped(de) {
    return !this.hasDataExceptSkipped(de);
  }
  /**
   * Only counts Algo-related inputs: temp, mens (but not Spotting), lh, pregTest, deviationReason (from flags).
   */
  hasAlgoRelatedInput(de) {
    return !!(de.temperature || de.mens === Mens.MENSTRUATION || de.lhTest || de.pregTest || de.dataFlags.some(f => DATA_FLAGS_USED_BY_ALGO.has(f)));
  }
  /**
   * Only counts data sent to the frontend
   */
  hasAppSupportedData(de) {
    const _de = {
      ...de
    }; // don't mutate
    APP_UNSUPPORTED_ENTRY_KEYS.forEach(key => delete _de[key]);
    return !this.isEmpty(_de);
  }
  /**
   * Mutates
   */
  normalizeDailyEntry(de) {
    // Remove all intensities for dataFlags that are not present in dataFlags
    de.dataFlagIntensity &&= Object.fromEntries(_stringMapEntries(de.dataFlagIntensity).filter(([flag]) => de.dataFlags.includes(Number(flag))));
    return de;
  }
}
export const dailyEntrySharedUtil = new DailyEntrySharedUtil();
function emptyTailOpimize(a) {
  let lastNonEmpty = -1;
  for (let i = a.length - 1; i >= 0; i--) {
    if (a[i] && !isEmptyObject(a[i])) {
      lastNonEmpty = i;
      break;
    }
  }
  return a.slice(0, lastNonEmpty + 1);
}
function isEmptyObject(value) {
  if (typeof value !== 'object') return false;
  return Object.keys(value).length === 0;
}